import { useEffect, useMemo, useState } from "react";
import { PencomCard } from "@/components/cards/PencomCard";
import { PageSkeleton } from "@/components/common/SkeletonLoader";
import { formatNGN } from "@/utils/moneyFormatter";
import { toast } from "@/utils/toast";
import {
  useGetPenalties,
  useGetPenaltyStatus,
  useDownloadPenaltiesScheduleXlsx,
  useGetPenaltiesBreakdown,
} from "@/hooks/compliance/compliance.hook";
import { PrimaryButton } from "@/components/buttons/PrimaryButton";
import { PenaltyEmployeesDialog } from "@/components/dashboard/PenaltyEmployeesDialog";
import { PenaltyScheduleDownloadDialog } from "@/components/dashboard/PenaltyScheduleDownloadDialog";
import { PenaltySummaryNotice } from "@/components/dashboard/PenaltySummaryNotice";
import {
  buildPenaltyBreakdown,
  type YearlyPenalty,
} from "@/utils/penaltyBreakdown";

export const PenaltyBreakdownPage = () => {
  const { data: penaltyStatus } = useGetPenaltyStatus();
  const { data: penalties, isLoading } = useGetPenalties();
  const {
    mutateAsync: downloadPenaltiesScheduleXlsx,
    isPending: isDownloadingPenaltiesScheduleXlsx,
  } = useDownloadPenaltiesScheduleXlsx();

  const [downloadConfirmOpen, setDownloadConfirmOpen] = useState(false);
  const [employeesDialog, setEmployeesDialog] = useState<{
    month: string;
    year: string;
    monthLabel: string;
  } | null>(null);

  const {
    data: penaltiesBreakdown,
    isFetching: isPenaltyBreakdownFetching,
    error: penaltiesBreakdownError,
  } = useGetPenaltiesBreakdown(
    employeesDialog?.month ?? "",
    employeesDialog?.year ?? "",
    employeesDialog !== null
  );

  const [expandedYear, setExpandedYear] = useState<number | null>(null);

  const toggleYear = (year: number) => {
    setExpandedYear((prev) => (prev === year ? null : year));
  };

  const breakdown = useMemo<YearlyPenalty[]>(
    () => buildPenaltyBreakdown(penalties),
    [penalties]
  );

  // Match the dialog UX: open the first available year by default.
  useEffect(() => {
    if (expandedYear === null && breakdown.length > 0) {
      setExpandedYear(breakdown[0].year);
    }
  }, [breakdown, expandedYear]);

  if (isLoading) {
    return <PageSkeleton tableColumns={6} tableRows={6} />;
  }

  const totalOutstandingPenalty = penaltyStatus?.totalOutstandingPenalty;

  const handleViewPenaltyEmployees = (
    year: number,
    month: string,
    monthLabel: string
  ) => {
    if (!month) return;
    setEmployeesDialog({
      month,
      year: String(year),
      monthLabel,
    });
  };

  const penaltyBreakdownError =
    penaltiesBreakdownError instanceof Error
      ? penaltiesBreakdownError
      : penaltiesBreakdownError
      ? new Error(String(penaltiesBreakdownError))
      : null;

  const handleDownloadSchedule = async () => {
    try {
      const blob = await downloadPenaltiesScheduleXlsx();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "Penalties-Schedule.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setDownloadConfirmOpen(false);
    } catch {
      toast.error(
        "Could not download the penalties schedule. Please try again."
      );
    }
  };
  return (
    <div className="w-full flex flex-col min-h-screen">
      <div className="w-full flex flex-col max-w-[1240px] mx-auto mt-6 px-4">
        <div className="flex flex-row justify-between items-start mb-6">
          <div className="flex flex-col gap-y-1">
            <p className="font-semibold text-2xl font-clash-display text-medium-dark-grey">
              Penalty Breakdown
            </p>
            {typeof totalOutstandingPenalty === "number" && (
              <p className="text-sm text-medium-dark-grey font-clash-display">
                Outstanding penalty:{" "}
                <span className="font-semibold text-dark-red">
                  {formatNGN(totalOutstandingPenalty)}
                </span>
              </p>
            )}
          </div>

          <PrimaryButton
            title="Download Penalty Schedule"
            isLoading={isDownloadingPenaltiesScheduleXlsx}
            onClick={() => setDownloadConfirmOpen(true)}
            disabled={isDownloadingPenaltiesScheduleXlsx}
          />
        </div>

        <div className="mb-6">
          <PenaltySummaryNotice penalties={penalties} />
        </div>

        <PencomCard>
          <div className="flex flex-col gap-y-6 mt-2">
            {/* Year sections */}
            <div className="flex flex-col gap-y-4">
              {breakdown.map((yearData) => (
                <div
                  key={yearData.year}
                  className="border border-stroke rounded-lg overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => toggleYear(yearData.year)}
                    className="flex items-center justify-between w-full px-4 sm:px-6 py-4 bg-[#FCFCF8] hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-x-3">
                      <img
                        src="/assets/icons/alert-fill.svg"
                        alt=""
                        className="w-6 h-6 sm:w-8 sm:h-8"
                      />
                      <p className="font-clash-display font-medium text-base text-[#404040]">
                        {yearData.year} -{" "}
                        <span className="font-semibold">
                          {formatNGN(yearData.total)}
                        </span>
                      </p>
                    </div>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      className={`transition-transform duration-200 ${
                        expandedYear === yearData.year ? "rotate-180" : ""
                      }`}
                    >
                      <path
                        d="M6 9L12 15L18 9"
                        stroke="#404040"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  {expandedYear === yearData.year && (
                    <div className="px-4 sm:px-6 pb-4 pt-2 sm:pt-4">
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[680px] border-collapse border border-stroke">
                          <thead>
                            <tr className="bg-[#FCFCF8]">
                              <th className="font-clash-display font-medium text-sm text-[#404040] text-left px-4 py-3 border-r border-stroke">
                                Month
                              </th>
                              <th className="font-clash-display font-medium text-sm text-[#404040] text-left px-4 py-3 border-r border-stroke">
                                Outstanding Amount
                              </th>
                              <th className="font-clash-display font-medium text-sm text-[#404040] text-left px-4 py-3 border-r border-stroke">
                                Number of Employees
                              </th>
                              <th className="font-clash-display font-medium text-sm text-[#404040] text-center px-4 py-3 w-px whitespace-nowrap">
                                Employees
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {yearData.months.map((monthData) => (
                              <tr
                                key={`${yearData.year}-${
                                  monthData.month || monthData.monthLabel
                                }`}
                                className="border-t border-stroke"
                              >
                                <td className="font-clash-display font-medium text-sm text-[#404040] px-4 py-3 border-r border-stroke">
                                  {monthData.monthLabel}
                                </td>
                                <td className="font-clash-display font-normal text-sm text-[#404040] px-4 py-3 border-r border-stroke tracking-[-0.084px]">
                                  {monthData.outstandingAmount !== null
                                    ? formatNGN(monthData.outstandingAmount)
                                    : "—"}
                                </td>
                                <td className="font-clash-display font-normal text-sm text-[#404040] px-4 py-3 border-r border-stroke">
                                  {monthData.employeeCount !== null
                                    ? monthData.employeeCount
                                    : "—"}
                                </td>
                                <td className="px-4 py-3 text-center align-middle">
                                  <button
                                    type="button"
                                    className="inline-flex items-center justify-center rounded-md p-1.5 text-[#404040] hover:bg-gray-100 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-button focus-visible:ring-offset-1 underline font-clash-display"
                                    aria-label={`View employees for ${monthData.monthLabel} ${yearData.year}`}
                                    disabled={!monthData.month}
                                    onClick={() =>
                                      handleViewPenaltyEmployees(
                                        yearData.year,
                                        monthData.month,
                                        monthData.monthLabel
                                      )
                                    }
                                  >
                                    View
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </PencomCard>
      </div>

      <PenaltyScheduleDownloadDialog
        open={downloadConfirmOpen}
        onOpenChange={setDownloadConfirmOpen}
        onConfirm={handleDownloadSchedule}
        isDownloading={isDownloadingPenaltiesScheduleXlsx}
      />

      <PenaltyEmployeesDialog
        open={employeesDialog !== null}
        onOpenChange={(open) => {
          if (!open) setEmployeesDialog(null);
        }}
        monthLabel={employeesDialog?.monthLabel ?? ""}
        year={employeesDialog ? Number(employeesDialog.year) : 0}
        isLoading={employeesDialog !== null && isPenaltyBreakdownFetching}
        error={penaltyBreakdownError}
        data={penaltiesBreakdown}
      />
    </div>
  );
};

export default PenaltyBreakdownPage;
